import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, ShoppingCart, Briefcase, Code2, HeartPulse, GraduationCap, Sparkles, CheckCircle2 } from 'lucide-react';
import { BookDemoModal } from '@/components/BookDemoModal';

const CASES = [
  {
    icon: Briefcase,
    industry: 'SaaS & B2B',
    color: '#FF6B1A',
    pitch: 'Compress outbound → demo → close. Replace the SDR + AE coaching stack with always-on agents.',
    agents: ['Velocity SDR', 'Closer Coach', 'Pipeline Hunter'],
    workflow: [
      'Velocity SDR researches accounts, drafts 3-step sequences',
      'Pipeline Hunter scores inbound leads BANT-style',
      'Closer Coach rehearses objections before the live call',
    ],
    impact: '+31% close rate · −$5K/mo enablement vendor',
  },
  {
    icon: ShoppingCart,
    industry: 'E-commerce',
    color: '#39FF14',
    pitch: 'Tier-1 support, refund triage, and ad creative — staffed by agents, with human escalation on policy edge cases.',
    agents: ['Support Concierge', 'Refund Captain', 'Creative Forge'],
    workflow: [
      'Support Concierge auto-handles 70% of tickets with empathy + KB writes',
      'Refund Captain decides APPROVE/PARTIAL/DENY with audit logs',
      'Creative Forge generates 5 Meta + 3 LinkedIn ads per launch',
    ],
    impact: '70% ticket deflection · 4× creative velocity',
  },
  {
    icon: Building2,
    industry: 'Fintech / Wealth',
    color: '#FFB17A',
    pitch: 'Compliance-aware support, qualification, and analyst synthesis — with auditable logs ready for examiners.',
    agents: ['Refund Captain', 'Insight Loom', 'SQL Sage'],
    workflow: [
      'Insight Loom synthesizes weekly market briefs',
      'SQL Sage answers data Qs without giving prod write access',
      'Refund Captain handles policy-bounded customer credits',
    ],
    impact: 'Audit-ready logs · 6× faster analyst output',
  },
  {
    icon: Code2,
    industry: 'Engineering / DevTools',
    color: '#39FF14',
    pitch: 'Quality gates, on-call relief, and dev-first documentation — from PR review to SQL self-serve.',
    agents: ['PR Reviewer', 'SQL Sage', 'Ops Architect'],
    workflow: [
      'PR Reviewer flags correctness risks before merge',
      'SQL Sage gives PMs ad-hoc data without bothering analysts',
      'Ops Architect drafts SOPs + failure modes for new services',
    ],
    impact: '+22% PR merge velocity · −30% analyst tickets',
  },
  {
    icon: HeartPulse,
    industry: 'Healthcare / RCM',
    color: '#FF6B1A',
    pitch: 'Claims follow-up, patient comms, and policy-bounded triage — with PHI-safe configurations.',
    agents: ['Cash Flow Whisperer', 'Support Concierge', 'Ops Architect'],
    workflow: [
      'Cash Flow Whisperer drives net collections via friendly nudges',
      'Support Concierge handles patient FAQ + appointment rescheduling',
      'Ops Architect maps clinic SOPs and failure modes',
    ],
    impact: '+18% net collections · 9× faster patient response',
  },
  {
    icon: GraduationCap,
    industry: 'Education / Bootcamps',
    color: '#FFB17A',
    pitch: 'Personalized cohort comms, applicant qualification, and content factory for high-velocity programs.',
    agents: ['Talent Sourcer', 'Landing Page Genius', 'Insight Loom'],
    workflow: [
      'Talent Sourcer writes JDs + outbound for staff hires',
      'Landing Page Genius spins variants per cohort',
      'Insight Loom synthesizes outcomes for marketing',
    ],
    impact: '3× cohort-page output · +12% applicant quality',
  },
];

export default function UseCases() {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} source="use_cases" />
      <div className="text-center max-w-3xl mx-auto mb-10">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// use cases</span>
        <h1 className="display text-3xl md:text-5xl font-semibold mt-2 leading-tight">
          Six industries. <span className="gradient-text">One revenue OS.</span>
        </h1>
        <p className="text-white/65 mt-3">
          We didn’t build a chatbot. We built revenue agents tuned to the workflows that actually move dollars in your business.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {CASES.map((c) => (
          <motion.div
            whileHover={{ y: -2 }}
            key={c.industry}
            className="card-base card-hover p-6 flex flex-col gap-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl grid place-items-center hairline" style={{ boxShadow: `0 0 18px ${c.color}33` }}>
                  <c.icon className="h-5 w-5" style={{ color: c.color }} />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-white/45">// industry</span>
                  <h3 className="display text-xl font-semibold leading-tight">{c.industry}</h3>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-widest hairline px-2 py-1 rounded-full text-[#39FF14]">{c.impact}</span>
            </div>
            <p className="text-sm text-white/75">{c.pitch}</p>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF6B1A] mb-1.5">// recommended agents</div>
              <div className="flex flex-wrap gap-1.5">
                {c.agents.map((a) => (
                  <span key={a} className="text-[11px] px-2.5 py-1 rounded-full hairline text-white/80">{a}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-1.5">// sample workflow</div>
              <ul className="space-y-1.5">
                {c.workflow.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle2 className="h-4 w-4 text-[#39FF14] mt-0.5 shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 card-base p-8 relative overflow-hidden">
        <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
        <div className="relative grid md:grid-cols-2 gap-6 items-center">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// don’t see your industry?</span>
            <h2 className="display text-2xl md:text-3xl font-semibold mt-2">We probably have a playbook anyway.</h2>
            <p className="text-white/65 mt-2 max-w-md text-sm">Tell us your top revenue workflow — we’ll wire it in a 30-minute call and project your first-90-day ROI.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <button onClick={() => setDemoOpen(true)} className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">
              <Sparkles className="h-4 w-4" /> Book a demo <ArrowRight className="h-4 w-4" />
            </button>
            <Link to="/marketplace" className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm">
              Browse all agents
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
